import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Alert,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Receipt as ReceiptIcon,
  Propane as PropaneIcon,
  Person as PersonIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { contractsApi, tanksApi } from '../../services/api';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import MobileCardList from '../../components/MobileCardList';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

const CONTRACT_TYPE_LABELS = {
  domestic: 'Domestico',
  commercial: 'Commerciale', 
  industrial: 'Industriale',
  agricultural: 'Agricolo',
  resale: 'Rivendita',
};

const ContractDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: contract, isLoading, error } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => contractsApi.getById(id).then(res => res.data)
  });

  if (isLoading) {
    return (
      <Box>
        <Skeleton height={60} sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}><Skeleton height={300} /></Grid>
          <Grid item xs={12} md={8}><Skeleton height={300} /></Grid>
        </Grid>
      </Box>
    );
  }

  if (error || !contract) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/crm/contratti')}>Torna alla lista</Button>
        <Alert severity="error" sx={{ mt: 2 }}>Contratto non trovato</Alert>
      </Box>
    );
  }

  const customerName = contract.Customer?.business_name || 
    `${contract.Customer?.first_name} ${contract.Customer?.last_name}`;

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 0 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <IconButton onClick={() => navigate('/crm/contratti')} size={isMobile ? 'small' : 'medium'}><ArrowBackIcon /></IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold" noWrap>
              Contratto {contract.contract_number}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
              <Chip label={CONTRACT_TYPE_LABELS[contract.contract_type] || contract.contract_type} size="small" variant="outlined" />
              <Chip label={contract.is_active ? 'Attivo' : 'Chiuso'} size="small" color={contract.is_active ? 'success' : 'default'} />
              {!isMobile && <Chip label={`Cod. ${contract.installation_code}`} size="small" variant="outlined" />}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<PersonIcon />} onClick={() => navigate(`/crm/clienti/${contract.customer_id}`)} size={isMobile ? 'small' : 'medium'} sx={{ flex: { xs: '1 1 0', sm: '0 0 auto' } }}>
            {isMobile ? 'Cliente' : 'Cliente'}
          </Button>
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/crm/contratti/${id}/modifica`)} size={isMobile ? 'small' : 'medium'} sx={{ flex: { xs: '1 1 0', sm: '0 0 auto' } }}>
            {isMobile ? 'Modifica' : 'Modifica'}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>Dati Contratto</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Cliente</Typography>
                  <Typography variant="body1" fontWeight="medium">{customerName}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Indirizzo Installazione</Typography>
                  <Typography variant="body2">{contract.installation_address}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {contract.installation_postal_code} {contract.installation_city} ({contract.installation_province})
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">Data Inizio</Typography>
                  <Typography variant="body2">
                    {contract.start_date ? format(new Date(contract.start_date), 'dd MMMM yyyy', { locale: it }) : '-'}
                  </Typography>
                </Box>
                {contract.end_date && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Data Fine</Typography>
                    <Typography variant="body2">
                      {format(new Date(contract.end_date), 'dd MMMM yyyy', { locale: it })}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>Condizioni Economiche</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Prezzo al Litro</Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    {formatCurrency(contract.price_per_unit)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Canone Annuale</Typography>
                  <Typography variant="body1">{formatCurrency(contract.annual_fee)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Deposito Cauzionale</Typography>
                  <Typography variant="body1">{formatCurrency(contract.deposit_amount)}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, mb: 2, gap: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  <PropaneIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Serbatoi
                </Typography>
                <Button variant="outlined" size="small" startIcon={<AddIcon />} fullWidth={isMobile}>
                  Aggiungi Serbatoio
                </Button>
              </Box>

              {isMobile ? (
                <MobileCardList
                  rows={contract.Tanks || []}
                  emptyMessage="Nessun serbatoio associato"
                  primaryField="serial_number"
                  secondaryField="tank_type"
                  statusField={{
                    field: 'status',
                    labelMap: {
                      active: 'Attivo',
                      expiring: 'In Scadenza',
                      expired: 'Scaduto',
                    },
                    colorMap: {
                      active: 'success',
                      expiring: 'warning',
                      expired: 'error',
                    },
                  }}
                  extraFields={[
                    {
                      field: 'capacity_liters',
                      label: 'Capacità',
                      render: (row) => `${row.capacity_liters || 0} Lt`,
                    },
                    {
                      field: 'installation_date',
                      label: 'Installazione',
                      render: (row) => row.installation_date ? format(new Date(row.installation_date), 'dd/MM/yyyy') : '-',
                    },
                    {
                      field: 'next_inspection_due',
                      label: 'Collaudo',
                      render: (row) => row.next_inspection_due ? format(new Date(row.next_inspection_due), 'dd/MM/yyyy') : '-',
                    },
                  ]}
                />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Matricola</TableCell>
                        <TableCell>Capacità</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Data Installazione</TableCell>
                        <TableCell>Prossimo Collaudo</TableCell>
                        <TableCell>Stato</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(contract.Tanks || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography color="text.secondary" sx={{ py: 2 }}>Nessun serbatoio associato</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        contract.Tanks.map((tank) => (
                          <TableRow key={tank.id} hover>
                            <TableCell><strong>{tank.serial_number}</strong></TableCell>
                            <TableCell>{tank.capacity_liters} Lt</TableCell>
                            <TableCell>{tank.tank_type}</TableCell>
                            <TableCell>
                              {tank.installation_date ? format(new Date(tank.installation_date), 'dd/MM/yyyy') : '-'}
                            </TableCell>
                            <TableCell>
                              {tank.next_inspection_due ? format(new Date(tank.next_inspection_due), 'dd/MM/yyyy') : '-'}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={tank.status === 'active' ? 'Attivo' : tank.status === 'expiring' ? 'In Scadenza' : 'Scaduto'}
                                size="small"
                                color={tank.status === 'active' ? 'success' : tank.status === 'expiring' ? 'warning' : 'error'}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, mb: 2, gap: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Ultime Fatture
                </Typography>
                <Button variant="outlined" size="small" onClick={() => navigate(`/crm/fatture?contract=${id}`)} fullWidth={isMobile}>
                  Vedi Tutte
                </Button>
              </Box>
              
              <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                Le fatture associate a questo contratto appariranno qui.
              </Typography>
            </CardContent>
          </Card>

          {contract.notes && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Note</Typography>
                <Typography variant="body2">{contract.notes}</Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContractDetail;
